import {Component, OnDestroy, OnInit} from '@angular/core';
import {gql} from '@apollo/client';
import {Apollo} from 'apollo-angular';
import {Subject, Subscription} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, startWith, switchMap} from 'rxjs/operators';
import {
  getChatrooms,
  getChatroomsVariables,
  getChatrooms_me_chatRooms_edges_node,
} from './__generated__/getChatrooms';
import {ErrorService} from '../shared/services/error.service';

const GET_CHATROOMS = gql`
  query getChatrooms($search: String) {
    me {
      id
      chatRooms(search: $search, orderBy: "-lastMessage", isCoachRoom: true) {
        edges {
          node {
            id
            members {
              id
              firstName
              lastName
            }
            messages(first: 1) {
              #Latest sent message
              edges {
                node {
                  id
                  sentBy {
                    id
                    firstName
                  }
                  unreadContentForUser
                  message
                  created
                }
              }
            }
          }
        }
      }
    }
  }
`;

@Component({
  selector: 'app-buddy-page',
  templateUrl: './buddy-page.component.html',
  styleUrls: ['./buddy-page.component.scss'],
})
export class BuddyPageComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  chatrooms: getChatrooms_me_chatRooms_edges_node[] | null = null;
  currentUserId: string | null = null;
  unreadChatrooms = new Set<string>();
  selectedChatroomId: string | null = null;
  search$ = new Subject<string>();

  constructor(
    private apollo: Apollo,
    private errorHandler: ErrorService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.search$
        .pipe(
          debounceTime(300),
          startWith(''),
          distinctUntilChanged(),
          switchMap(search =>
            this.apollo
              .watchQuery<getChatrooms, getChatroomsVariables>({
                query: GET_CHATROOMS,
                variables: {
                  search: search,
                },
              })
              .valueChanges.pipe(catchError(error => this.errorHandler.handleApollo(error))),
          ),
        )
        .subscribe(output => {
          this.currentUserId = output.data.me.id;
          this.chatrooms = output.data.me.chatRooms.edges.map(c => c.node);
          this.chatrooms = this.chatrooms.map(room => {
            const newMembers = room.members.filter(member => member.id !== this.currentUserId);
            return {
              ...room,
              members: newMembers,
            };
          });

          this.checkUnseen(this.chatrooms);
          // Cache eviction and refetching of messages marks the currently selected chatroom as unread.
          if (this.selectedChatroomId) {
            this.unreadChatrooms.delete(this.selectedChatroomId);
          }
        }),
    );
  }

  checkUnseen(rooms: getChatrooms_me_chatRooms_edges_node[]): void {
    rooms.forEach(r => {
      const unseenMessages = r.messages.edges.filter(m => m.node.unreadContentForUser === true);
      if (unseenMessages.length > 0) {
        this.unreadChatrooms.add(r.id);
      }
    });
  }

  selectChatroom(chatroomId: string): void {
    if (this.selectedChatroomId === chatroomId) {
      return;
    }
    if (!this.chatrooms) {
      return;
    }
    this.selectedChatroomId = chatroomId;
  }

  onSearchValueChange(term: string): void {
    this.search$.next(term);
  }

  onChatroomSeen(id: string) {
    this.unreadChatrooms.delete(id);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
